import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { EmptyState } from '../common/EmptyState';
import { Select } from '../common/Select';
import { Toggle } from '../common/Toggle';
import { useToast } from '../../contexts/ToastContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { apiClient } from '../../services/api';
import type {
  CreateSystemSlaPolicyTemplateDto,
  SlaPriorityTarget,
  SystemSlaPolicyTemplateDto,
  UpdateSystemSlaPolicyTemplateDto,
} from '../../types';

const NEW_TEMPLATE_KEY = '__new__';

interface SlaPolicyDraft {
  name: string;
  description: string;
  isDefault: boolean;
  businessHoursOnly: boolean;
  targets: SlaPriorityTarget[];
}

const DEFAULT_PRIORITY_TARGETS: Array<Pick<SlaPriorityTarget, 'priorityName' | 'responseTimeMinutes' | 'resolutionTimeMinutes'>> = [
  { priorityName: 'P1', responseTimeMinutes: 15, resolutionTimeMinutes: 60 },
  { priorityName: 'P2', responseTimeMinutes: 30, resolutionTimeMinutes: 240 },
  { priorityName: 'P3', responseTimeMinutes: 120, resolutionTimeMinutes: 1440 },
  { priorityName: 'P4', responseTimeMinutes: 480, resolutionTimeMinutes: 4320 },
  { priorityName: 'P5', responseTimeMinutes: 1440, resolutionTimeMinutes: 10080 },
];

function asArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function toTargetDraft(target: SlaPriorityTarget): SlaPriorityTarget {
  return {
    ...target,
    priorityId: target.priorityId ?? createLocalId('priority'),
    priorityName: target.priorityName ?? '',
    responseTimeMinutes: target.responseTimeMinutes ?? null,
    resolutionTimeMinutes: target.resolutionTimeMinutes ?? null,
  };
}

function createDefaultTargets(): SlaPriorityTarget[] {
  return DEFAULT_PRIORITY_TARGETS.map((target, index) => ({
    priorityId: `p${index + 1}`,
    priorityName: target.priorityName,
    responseTimeMinutes: target.responseTimeMinutes,
    resolutionTimeMinutes: target.resolutionTimeMinutes,
  }));
}

function toDraft(template: SystemSlaPolicyTemplateDto): SlaPolicyDraft {
  const templateTargets = asArray(template.targets);
  const targets = templateTargets.length > 0 ? templateTargets.map(toTargetDraft) : createDefaultTargets();

  return {
    name: template.name ?? '',
    description: template.description ?? '',
    isDefault: Boolean(template.isDefault),
    businessHoursOnly: Boolean(template.businessHoursOnly),
    targets,
  };
}

function createDefaultDraft(referenceTargets?: SlaPriorityTarget[]): SlaPolicyDraft {
  const sourceTargets = asArray(referenceTargets);
  const targets = sourceTargets.length > 0 ? sourceTargets.map(toTargetDraft) : createDefaultTargets();

  return {
    name: '',
    description: '',
    isDefault: false,
    businessHoursOnly: true,
    targets,
  };
}

function formatMinutes(minutes: number | null): string {
  if (minutes === null || !Number.isFinite(minutes) || minutes <= 0) {
    return 'Disabled';
  }

  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
}

function parseMinutesInput(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return Math.max(1, Math.round(parsed));
}

function createLocalId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeTemplate(rawTemplate: SystemSlaPolicyTemplateDto, index: number): SystemSlaPolicyTemplateDto {
  const safeTemplate = rawTemplate ?? ({} as SystemSlaPolicyTemplateDto);
  return {
    ...safeTemplate,
    id: typeof safeTemplate.id === 'string' && safeTemplate.id.trim() ? safeTemplate.id : `sla-template-${index + 1}`,
    name: typeof safeTemplate.name === 'string' && safeTemplate.name.trim() ? safeTemplate.name : `SLA Template ${index + 1}`,
    description: typeof safeTemplate.description === 'string' ? safeTemplate.description : '',
    isDefault: Boolean(safeTemplate.isDefault),
    businessHoursOnly: Boolean(safeTemplate.businessHoursOnly),
    targets: asArray(safeTemplate.targets).map((target, targetIndex) => ({
      priorityId:
        typeof target?.priorityId === 'string' && target.priorityId.trim()
          ? target.priorityId
          : `priority-${targetIndex + 1}`,
      priorityName:
        typeof target?.priorityName === 'string' && target.priorityName.trim() ? target.priorityName : `P${targetIndex + 1}`,
      responseTimeMinutes:
        target?.responseTimeMinutes === null || (Number.isFinite(target?.responseTimeMinutes) && target.responseTimeMinutes > 0)
          ? target.responseTimeMinutes
          : null,
      resolutionTimeMinutes:
        target?.resolutionTimeMinutes === null || (Number.isFinite(target?.resolutionTimeMinutes) && target.resolutionTimeMinutes > 0)
          ? target.resolutionTimeMinutes
          : null,
    })),
    createdAt: typeof safeTemplate.createdAt === 'string' ? safeTemplate.createdAt : '',
    updatedAt: typeof safeTemplate.updatedAt === 'string' ? safeTemplate.updatedAt : undefined,
  };
}

export function SlaPoliciesTab() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);
  const [newTemplateDraft, setNewTemplateDraft] = useState<SlaPolicyDraft | null>(null);
  const [draftsByTemplateId, setDraftsByTemplateId] = useState<Record<string, SlaPolicyDraft>>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEscapeKey(() => setShowDeleteDialog(false), showDeleteDialog);

  const templatesQuery = useQuery<SystemSlaPolicyTemplateDto[]>({
    queryKey: ['itil', 'sla-policy-templates'],
    queryFn: async () => {
      try {
        const response = await apiClient.getSystemSlaPolicyTemplates();
        if (!Array.isArray(response)) {
          throw new Error('SLA policy templates response is not an array.');
        }
        return response.map((template, index) => normalizeTemplate(template, index));
      } catch (error) {
        throw new Error(errorMessage(error, 'Failed to load SLA policy templates.'));
      }
    },
  });

  const templates = Array.isArray(templatesQuery.data) ? templatesQuery.data : [];
  const isEditingNew = selectedTemplateKey === NEW_TEMPLATE_KEY;
  const requestedTemplateId =
    selectedTemplateKey && selectedTemplateKey !== NEW_TEMPLATE_KEY ? selectedTemplateKey : null;
  const selectedExistingTemplateId =
    requestedTemplateId && templates.some((template) => template.id === requestedTemplateId)
      ? requestedTemplateId
      : templates[0]?.id ?? null;
  const selectedTemplate = selectedExistingTemplateId
    ? templates.find((template) => template.id === selectedExistingTemplateId) ?? null
    : null;

  const activeDraft = useMemo(() => {
    if (isEditingNew) {
      return newTemplateDraft;
    }

    if (!selectedTemplate) {
      return null;
    }

    return draftsByTemplateId[selectedTemplate.id] ?? toDraft(selectedTemplate);
  }, [draftsByTemplateId, isEditingNew, newTemplateDraft, selectedTemplate]);

  const upsertMutation = useMutation({
    mutationFn: async (draft: SlaPolicyDraft) => {
      try {
        const basePayload: CreateSystemSlaPolicyTemplateDto = {
          name: draft.name.trim(),
          description: draft.description.trim() || undefined,
          isDefault: draft.isDefault,
          businessHoursOnly: draft.businessHoursOnly,
          targets: draft.targets.map((target) => ({
            priorityId: target.priorityId,
            priorityName: target.priorityName.trim(),
            responseTimeMinutes: target.responseTimeMinutes,
            resolutionTimeMinutes: target.resolutionTimeMinutes,
          })),
        };

        if (isEditingNew) {
          return await apiClient.createSystemSlaPolicyTemplate(basePayload);
        }

        if (!selectedTemplate) {
          throw new Error('No template selected.');
        }

        const updatePayload: UpdateSystemSlaPolicyTemplateDto = {
          id: selectedTemplate.id,
          ...basePayload,
        };
        return await apiClient.updateSystemSlaPolicyTemplate(selectedTemplate.id, updatePayload);
      } catch (error) {
        throw new Error(errorMessage(error, 'Failed to save SLA policy template.'));
      }
    },
    onSuccess: async (savedTemplate) => {
      await queryClient.invalidateQueries({ queryKey: ['itil', 'sla-policy-templates'] });
      addToast('SLA policy template saved successfully.', 'success');
      if (isEditingNew) {
        setNewTemplateDraft(null);
      }
      const savedTemplateId = savedTemplate?.id ?? null;
      setSelectedTemplateKey(savedTemplateId);
      setDraftsByTemplateId((current) => {
        if (!savedTemplateId) {
          return current;
        }
        const next = { ...current };
        delete next[savedTemplateId];
        return next;
      });
    },
    onError: (error) => {
      addToast(errorMessage(error, 'Failed to save SLA policy template.'), 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (templateId: string) => {
      try {
        await apiClient.deleteSystemSlaPolicyTemplate(templateId);
      } catch (error) {
        throw new Error(errorMessage(error, 'Failed to delete SLA policy template.'));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['itil', 'sla-policy-templates'] });
      addToast('SLA policy template deleted.', 'success');
      setShowDeleteDialog(false);
      setSelectedTemplateKey(null);
      setDraftsByTemplateId({});
    },
    onError: (error) => {
      addToast(errorMessage(error, 'Failed to delete SLA policy template.'), 'error');
    },
  });

  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: template.isDefault ? `${template.name} (Default)` : template.name,
  }));
  if (newTemplateDraft) {
    templateOptions.push({
      value: NEW_TEMPLATE_KEY,
      label: newTemplateDraft.name.trim() || 'New template (unsaved)',
    });
  }

  const selectedValue = isEditingNew ? NEW_TEMPLATE_KEY : selectedTemplate?.id ?? '';

  const updateDraft = (updater: (draft: SlaPolicyDraft) => SlaPolicyDraft) => {
    if (isEditingNew) {
      setNewTemplateDraft((current) => (current ? updater(current) : current));
      return;
    }

    if (!selectedTemplate) {
      return;
    }

    setDraftsByTemplateId((current) => ({
      ...current,
      [selectedTemplate.id]: updater(current[selectedTemplate.id] ?? toDraft(selectedTemplate)),
    }));
  };

  const updateTarget = (priorityId: string, updater: (target: SlaPriorityTarget) => SlaPriorityTarget) => {
    updateDraft((draft) => ({
      ...draft,
      targets: draft.targets.map((target) => (target.priorityId === priorityId ? updater(target) : target)),
    }));
  };

  const handleAddPriority = () => {
    updateDraft((draft) => {
      const nextPriorityNumber = draft.targets.length + 1;
      return {
        ...draft,
        targets: [
          ...draft.targets,
          {
            priorityId: createLocalId('priority'),
            priorityName: `P${nextPriorityNumber}`,
            responseTimeMinutes: null,
            resolutionTimeMinutes: null,
          },
        ],
      };
    });
  };

  const handleRemovePriority = (priorityId: string) => {
    updateDraft((draft) => ({
      ...draft,
      targets: draft.targets.filter((target) => target.priorityId !== priorityId),
    }));
  };

  const handleReset = () => {
    if (isEditingNew) {
      const referenceTargets = templates[0]?.targets;
      setNewTemplateDraft(createDefaultDraft(referenceTargets));
      return;
    }

    if (!selectedTemplate) {
      return;
    }

    setDraftsByTemplateId((current) => {
      const next = { ...current };
      delete next[selectedTemplate.id];
      return next;
    });
  };

  const validateDraft = (draft: SlaPolicyDraft): string | null => {
    const name = draft.name.trim();
    if (!name) {
      return 'Template name is required.';
    }

    const hasDuplicateTemplateName = templates.some(
      (template) =>
        template.name.trim().toLowerCase() === name.toLowerCase() &&
        (isEditingNew || template.id !== selectedTemplate?.id),
    );
    if (hasDuplicateTemplateName) {
      return 'Template name must be unique.';
    }

    if (draft.targets.length === 0) {
      return 'At least one priority target is required.';
    }

    for (const target of draft.targets) {
      if (!target.priorityName.trim()) {
        return 'Each target row requires a priority name.';
      }

      if (target.responseTimeMinutes !== null && target.responseTimeMinutes < 1) {
        return 'First response targets must be at least 1 minute when enabled.';
      }

      if (target.resolutionTimeMinutes !== null && target.resolutionTimeMinutes < 1) {
        return 'Resolution targets must be at least 1 minute when enabled.';
      }
    }

    return null;
  };

  const handleSave = () => {
    if (!activeDraft) {
      return;
    }

    const validationError = validateDraft(activeDraft);
    if (validationError) {
      addToast(validationError, 'error');
      return;
    }

    upsertMutation.mutate(activeDraft);
  };

  const isLoading = templatesQuery.isLoading;
  const isError = templatesQuery.isError;

  if (isLoading) {
    return (
      <div className="card">
        <p className="text-sm text-gray-600 dark:text-gray-300">Loading SLA policy templates…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card rounded border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
        Unable to load SLA policy templates. {errorMessage(templatesQuery.error, '')}
      </div>
    );
  }

  if (!activeDraft && templates.length === 0) {
    return (
      <EmptyState
        title="No SLA policy templates"
        message="Create your first SLA policy template."
        action={
          <button
            type="button"
            onClick={() => {
              setNewTemplateDraft(createDefaultDraft());
              setSelectedTemplateKey(NEW_TEMPLATE_KEY);
            }}
            className="rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700"
          >
            Create New Template
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <section className="card space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[280px] flex-1">
            <Select
              id="sla-template-select"
              label="Template"
              value={selectedValue}
              onChange={setSelectedTemplateKey}
              options={templateOptions}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              const referenceTargets = templates[0]?.targets;
              setNewTemplateDraft(createDefaultDraft(referenceTargets));
              setSelectedTemplateKey(NEW_TEMPLATE_KEY);
            }}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            Create New Template
          </button>
        </div>
      </section>

      {activeDraft ? (
        <section className="card space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="sla-template-name">
                Name
              </label>
              <input
                id="sla-template-name"
                type="text"
                value={activeDraft.name}
                onChange={(event) => updateDraft((draft) => ({ ...draft, name: event.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="sla-template-description">
                Description
              </label>
              <textarea
                id="sla-template-description"
                value={activeDraft.description}
                onChange={(event) => updateDraft((draft) => ({ ...draft, description: event.target.value }))}
                className="input min-h-[88px]"
              />
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div className="flex flex-wrap gap-6">
              <Toggle
                id="sla-business-hours-only"
                label="Business Hours Only"
                checked={activeDraft.businessHoursOnly}
                onChange={(checked) => updateDraft((draft) => ({ ...draft, businessHoursOnly: checked }))}
              />
              <Toggle
                id="sla-template-default"
                label="Is Default"
                checked={activeDraft.isDefault}
                onChange={(checked) => updateDraft((draft) => ({ ...draft, isDefault: checked }))}
              />
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              When enabled, SLA timers pause outside configured business hours.
            </p>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-left text-sm">
              <caption className="sr-only">SLA priority targets</caption>
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-3 font-semibold">Priority Level</th>
                  <th className="px-3 py-3 font-semibold">FR Enabled</th>
                  <th className="px-3 py-3 font-semibold">Res Enabled</th>
                  <th className="px-3 py-3 font-semibold">First Response (min)</th>
                  <th className="px-3 py-3 font-semibold">Resolution (min)</th>
                  <th className="px-3 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {activeDraft.targets.map((target) => (
                  <tr key={target.priorityId}>
                    <td className="px-3 py-3">
                      <input
                        type="text"
                        value={target.priorityName}
                        onChange={(event) =>
                          updateTarget(target.priorityId, (currentTarget) => ({
                            ...currentTarget,
                            priorityName: event.target.value,
                          }))
                        }
                        className="input min-w-[160px]"
                        aria-label={`Priority name for ${target.priorityId}`}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <Toggle
                        id={`fr-enabled-${target.priorityId}`}
                        label="Enabled"
                        checked={target.responseTimeMinutes !== null}
                        onChange={(checked) =>
                          updateTarget(target.priorityId, (currentTarget) => ({
                            ...currentTarget,
                            responseTimeMinutes: checked ? (currentTarget.responseTimeMinutes ?? 15) : null,
                          }))
                        }
                      />
                    </td>
                    <td className="px-3 py-3">
                      <Toggle
                        id={`resolution-enabled-${target.priorityId}`}
                        label="Enabled"
                        checked={target.resolutionTimeMinutes !== null}
                        onChange={(checked) =>
                          updateTarget(target.priorityId, (currentTarget) => ({
                            ...currentTarget,
                            resolutionTimeMinutes: checked ? (currentTarget.resolutionTimeMinutes ?? 60) : null,
                          }))
                        }
                      />
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        disabled={target.responseTimeMinutes === null}
                        value={target.responseTimeMinutes ?? ''}
                        onChange={(event) =>
                          updateTarget(target.priorityId, (currentTarget) => ({
                            ...currentTarget,
                            responseTimeMinutes: parseMinutesInput(event.target.value),
                          }))
                        }
                        className="input max-w-[180px] disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label={`First response minutes for ${target.priorityName || target.priorityId}`}
                      />
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{formatMinutes(target.responseTimeMinutes)}</p>
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        disabled={target.resolutionTimeMinutes === null}
                        value={target.resolutionTimeMinutes ?? ''}
                        onChange={(event) =>
                          updateTarget(target.priorityId, (currentTarget) => ({
                            ...currentTarget,
                            resolutionTimeMinutes: parseMinutesInput(event.target.value),
                          }))
                        }
                        className="input max-w-[180px] disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label={`Resolution minutes for ${target.priorityName || target.priorityId}`}
                      />
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{formatMinutes(target.resolutionTimeMinutes)}</p>
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => handleRemovePriority(target.priorityId)}
                        className="rounded-md border border-red-300 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                        aria-label={`Remove ${target.priorityName || target.priorityId}`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <button
              type="button"
              onClick={handleAddPriority}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
            >
              Add Priority
            </button>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={upsertMutation.isPending}
              className="rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-primary-600 dark:hover:bg-primary-700"
            >
              {upsertMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={upsertMutation.isPending}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
            >
              Reset
            </button>
            {!isEditingNew && selectedTemplate ? (
              <button
                type="button"
                onClick={() => setShowDeleteDialog(true)}
                className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
              >
                Delete
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          if (selectedTemplate) {
            deleteMutation.mutate(selectedTemplate.id);
          }
        }}
        title="Delete template?"
        message="This will permanently remove the selected SLA policy template."
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
